import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/firestore';
import _ from 'lodash';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import firebase from 'firebase/app';
import 'firebase/firestore';

@Injectable({
  providedIn: 'root',
})
export class AppParamsService {
  private COLLECTION_NAME = 'params';
  private watchers: Array<AppWatchers> = [];

  constructor(private db: AngularFirestore) {}

  watchFor(auctionId: string, fieldName: string) {
    const sectionName = this.inferSectionName(fieldName);

    const instanceWatcher = this.watchers.find((w) => w.key === `${sectionName}:${fieldName}`);
    if (instanceWatcher) {
      return instanceWatcher.observable;
    }

    const subject = new Subject();
    const newWatcher = this.db
      .collection(auctionId)
      .doc('sections')
      .collection(this.COLLECTION_NAME)
      .doc(sectionName)
      .collection('fields')
      .doc(fieldName)
      .valueChanges()
      .pipe(
        distinctUntilChanged((a, b) => _.isEqual(a, b)),
        map((d: any) => (!d || d.value === null || d.value === undefined || d.value === '' ? d?.default : d.value)),
        takeUntil(subject),
      );

    this.watchers.push({
      key: `${sectionName}:${fieldName}`,
      observable: newWatcher,
      subject: subject,
    });

    return newWatcher;
  }

  dispose(fieldName: string) {
    return new Promise<void>((resolve) => {
      const sectionName = this.inferSectionName(fieldName);

      const instanceWatcherIndex = this.watchers.findIndex((w) => w.key === `${sectionName}:${fieldName}`);

      if (instanceWatcherIndex >= 0) {
        const watcher = this.watchers.splice(instanceWatcherIndex, 1)[0];
        watcher.subject.next();
        watcher.subject.complete();
      }

      setTimeout(() => {
        resolve();
      }, 200);
    });
  }

  private inferSectionName(fieldName: any) {
    if (fieldName in AppParamGeneral) {
      return 'general';
    }
    if (fieldName in AppParamLots) {
      return 'lots';
    }
    if (fieldName in AppParamMultimedia) {
      return 'multimedia';
    }
    if (fieldName in AppParamSecurity) {
      return 'security';
    }
    if (fieldName in AppParamAdmin) {
      return 'admin';
    }

    throw Error(`field ${fieldName} not belogns to a valid section`);
  }
}

interface AppWatchers {
  key: string;
  observable: Observable<firebase.firestore.DocumentData>;
  subject: Subject<any>;
}

enum AppParamGeneral {
  contactNumber = 'contactNumber',
  lotDocument = 'lotDocument',
  lotDocumentName = 'lotDocumentName',
  terms = 'terms',
  wellcome = 'wellcome',
  exchangeRate = 'exchangeRate',
  primaryCurrencyType = 'primaryCurrencyType',
}

enum AppParamLots {
  lotResumeDownload = 'lotResumeDownload',
  lotResumeView = 'lotResumeView',
  lotResumeVideoView = 'lotResumeVideoView',
  lotEditionWhenActive = 'lotEditionWhenActive',
  lotCutPercentage = 'lotCutPercentage',
  lotScheduledBid = 'lotScheduledBid',
}

enum AppParamMultimedia {
  lotInfoLegend = 'lotInfoLegend',
  trEmbeded = 'trEmbeded',
}

enum AppParamSecurity {
  anonymAccessEnabled = 'anonymAccessEnabled',
  userBiometricAuth = 'userBiometricAuth',
  userSelfManagement = 'userSelfManagement',
  userAccessCodeLen = 'userAccessCodeLen',
}

enum AppParamAdmin {
  soundWhenBidArrives = 'soundWhenBidArrives',
  soundPresencialWhenBidArrives = 'soundPresencialWhenBidArrives',
  shortcutForCutBid = 'shortcutForCutBid',
  shortcutForDeleteLastBid = 'shortcutForDeleteLastBid',
}

export const AppParamItems = {
  AppParamGeneral,
  AppParamLots,
  AppParamMultimedia,
  AppParamSecurity,
  AppParamAdmin,
};
